import type { AudioClip, Project } from './types';
import { clipEndTicks, tempoMapOf } from './selectors';
import { secondsSpanToTicks, secondsToTicks, ticksToSeconds } from '@/timing/tempoMap';

/**
 * Overlaps nobody asked for.
 *
 * Two clips on the same lane that overlap both play, and both play at full
 * level. Where that was intended — a crossfade, a deliberate layer — it is
 * fine. Where it was a drag that went a little too far, it is two takes
 * fighting: doubled level, and comb filtering wherever the two are correlated.
 * Nothing in the timeline said it had happened.
 *
 * ## What counts as bare
 *
 * Not "these clips overlap": a crossfade *is* an overlap, and so is a layer
 * somebody built on purpose. What matters is whether anything is **covering**
 * it. The outgoing clip's fade-out covers the end of the overlap and the
 * incoming clip's fade-in covers the start, so the region left over is
 *
 *     [ incoming start + fade in , outgoing end - fade out )
 *
 * and that is where both clips are at full level. A proper crossfade makes it
 * empty, because both fades span the whole overlap. A 10ms crossfade dropped
 * onto a 400ms overlap does not: 380ms of doubled audio is still there, and
 * calling that handled because a fade exists somewhere in it would be exactly
 * the kind of claim this project does not make.
 *
 * Lanes are kept apart because clips only sum within a lane — a take sitting
 * under the comp is not heard at all, so it cannot collide with anything.
 */
export interface BareOverlap {
  trackId: string;
  takeLane: number;
  outgoingId: string;
  incomingId: string;
  /** The whole overlapping span, which is what a crossfade would cover. */
  startTicks: number;
  endTicks: number;
  /** The part of it no fade covers — where both clips are at full level. */
  bareStartTicks: number;
  bareEndTicks: number;
}

/**
 * Below this an overlap is rounding rather than an edit.
 *
 * Positions are integers in ticks while fades are in seconds, so a clip's end
 * and its neighbour's start routinely differ by a fraction of a tick after a
 * split or a comp. `crossfade.ts` draws the same line for the same reason.
 */
const BARE_TOLERANCE_SEC = 0.001;

/** Every uncovered overlap on one lane, earliest first. */
export function bareOverlapsOnLane(project: Project, trackId: string, takeLane = 0): BareOverlap[] {
  const track = project.tracks[trackId];
  if (!track) return [];

  const clips = track.clipIds
    .map((id) => project.clips[id])
    .filter((clip): clip is AudioClip => Boolean(clip) && clip.kind === 'audio' && clip.takeLane === takeLane)
    .sort((a, b) => a.startTicks - b.startTicks);

  const map = tempoMapOf(project);
  const found: BareOverlap[] = [];

  for (let i = 1; i < clips.length; i++) {
    const outgoing = clips[i - 1];
    const incoming = clips[i];
    const endTicks = clipEndTicks(project, outgoing);
    if (endTicks <= incoming.startTicks) continue;

    // Where each fade reaches, in ticks, from the edge it starts at. The
    // fade-out is measured backwards from the clip's end, so it goes through
    // seconds rather than a tick span — a span is anchored at its start.
    const fadeInTicks = secondsSpanToTicks(map, incoming.startTicks, incoming.fadeIn.durationSec);
    const bareStartTicks = incoming.startTicks + fadeInTicks;
    const bareEndTicks = Math.min(
      endTicks,
      secondsToTicks(map, ticksToSeconds(map, endTicks) - outgoing.fadeOut.durationSec),
    );

    const bareSec = ticksToSeconds(map, bareEndTicks) - ticksToSeconds(map, bareStartTicks);
    if (bareSec <= BARE_TOLERANCE_SEC) continue;

    found.push({
      trackId,
      takeLane,
      outgoingId: outgoing.id,
      incomingId: incoming.id,
      startTicks: incoming.startTicks,
      endTicks,
      bareStartTicks,
      bareEndTicks,
    });
  }

  return found;
}

/** Every uncovered overlap in the project, over every track and lane. */
export function bareOverlaps(project: Project): BareOverlap[] {
  const found: BareOverlap[] = [];
  for (const trackId of project.trackOrder) {
    const track = project.tracks[trackId];
    if (!track) continue;
    const lanes = new Set<number>();
    for (const clipId of track.clipIds) {
      const clip = project.clips[clipId];
      if (clip && clip.kind === 'audio') lanes.add(clip.takeLane);
    }
    for (const lane of [...lanes].sort((a, b) => a - b)) {
      found.push(...bareOverlapsOnLane(project, trackId, lane));
    }
  }
  return found;
}

/** How much of a project is playing two clips at once, in seconds. */
export function bareOverlapSeconds(project: Project, overlap: BareOverlap): number {
  const map = tempoMapOf(project);
  return Math.max(0, ticksToSeconds(map, overlap.bareEndTicks) - ticksToSeconds(map, overlap.bareStartTicks));
}

/** The whole overlapping span in seconds — what a crossfade would have to cover. */
export function overlapSeconds(project: Project, overlap: BareOverlap): number {
  const map = tempoMapOf(project);
  return Math.max(0, ticksToSeconds(map, overlap.endTicks) - ticksToSeconds(map, overlap.startTicks));
}
